import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';

const resultText = (attempt) => {
  const score = Number(attempt?.score || 0);
  const totalMarks = Number(attempt?.totalMarks || 0);
  const percentage = Number(attempt?.percentage || 0);
  return totalMarks > 0 ? `${score}/${totalMarks} (${percentage}%)` : `${percentage}%`;
};

export default function LinkedQuizAttempt({ lesson, onCompleted, notify, accessContext = '' }) {
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quiz, setQuiz] = useState(lesson?.linkedQuiz || null);
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [message, setMessage] = useState('');

  const showMessage = useCallback((text, type = 'danger') => {
    setMessage(text || '');
    notify?.(text, type);
  }, [notify]);

  const loadQuiz = useCallback(async () => {
    if (!lesson?._id) return;
    setLoading(true);
    setMessage('');
    try {
      const response = await api.get(`/training/on-demand/lessons/${lesson._id}/quiz`, {
        params: accessContext ? { accessContext } : undefined,
      });
      const payload = response.data?.data || {};
      setQuiz(payload.quiz || null);
      setAttempt(payload.attempt || null);
    } catch (err) {
      showMessage(err.response?.data?.message || 'Could not load this quiz.');
    } finally {
      setLoading(false);
    }
  }, [accessContext, lesson?._id, showMessage]);

  useEffect(() => { loadQuiz(); }, [loadQuiz]);

  const startQuiz = async () => {
    setStarting(true);
    setMessage('');
    try {
      const response = await api.post(`/training/on-demand/lessons/${lesson._id}/quiz/start`, {
        accessContext,
      });
      const payload = response.data?.data || {};
      setQuiz(payload.quiz || quiz);
      setAttempt(payload.attempt || null);
    } catch (err) {
      showMessage(err.response?.data?.message || 'Could not start this quiz.');
    } finally {
      setStarting(false);
    }
  };

  const submitQuiz = async () => {
    const questions = quiz?.questions || [];
    if (questions.some((question) => {
      const answer = answers[String(question._id)];
      if (!answer) return true;
      return question.type === 'fill_blank'
        ? !String(answer.textAnswer || '').trim()
        : !answer.selectedOptionId;
    })) {
      showMessage('Answer every quiz question before submitting.', 'warning');
      return;
    }

    setSubmitting(true);
    setMessage('');
    try {
      const response = await api.post(`/training/on-demand/lessons/${lesson._id}/quiz/submit`, {
        accessContext,
        attemptId: attempt?._id,
        answers: questions.map((question) => ({
          questionId: question._id,
          selectedOptionId: answers[String(question._id)]?.selectedOptionId || null,
          textAnswer: answers[String(question._id)]?.textAnswer || '',
        })),
      });
      const completedAttempt = response.data?.data?.attempt || null;
      setAttempt(completedAttempt);
      showMessage(
        completedAttempt?.resultReleased === false
          ? 'Quiz submitted. Your result will be shown after review.'
          : `Quiz submitted. Mark: ${resultText(completedAttempt)}.`,
        completedAttempt?.passed ? 'success' : 'warning'
      );
      await onCompleted?.();
    } catch (err) {
      showMessage(err.response?.data?.message || 'Could not submit this quiz.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="py-4 text-center"><span className="spinner-border spinner-border-sm text-primary" /></div>;
  if (!quiz) return <div className="alert alert-warning py-2 mb-3">{message || 'This quiz is not currently available.'}</div>;

  if (attempt?.status === 'completed') {
    const resultReleased = attempt.resultReleased !== false;
    return (
      <div className={`alert ${resultReleased ? (attempt.passed ? 'alert-success' : 'alert-warning') : 'alert-info'} d-flex align-items-center gap-2 mb-3`}>
        <i className={`bi ${resultReleased ? 'bi-check-circle-fill' : 'bi-hourglass-split'}`} />
        <span>
          {resultReleased
            ? <>Quiz completed. Mark: <strong>{resultText(attempt)}</strong></>
            : 'Quiz completed. Your mark will be available after review.'}
        </span>
      </div>
    );
  }

  if (!attempt) {
    return (
      <div className="border rounded-4 p-3 mb-3 bg-light">
        {message && <div className="alert alert-danger py-2 small">{message}</div>}
        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
          <div>
            <div className="fw-semibold">{quiz.title}</div>
            <div className="small text-muted">
              {quiz.questionCount} questions{quiz.totalDuration ? ` | ${quiz.totalDuration} minutes` : ''}
            </div>
          </div>
          <button type="button" className="btn btn-primary btn-sm" disabled={starting} onClick={startQuiz}>
            {starting ? 'Starting...' : 'Attend Quiz'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-4 p-3 mb-3 bg-white">
      <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
        <div className="fw-semibold">{quiz.title}</div>
        <span className="badge text-bg-light border">{lesson.quizRequirement === 'compulsory' ? 'Compulsory' : 'Optional'}</span>
      </div>
      {message && <div className="alert alert-warning py-2 small">{message}</div>}
      <div className="d-flex flex-column gap-3">
        {(quiz.questions || []).map((question, index) => (
          <div key={question._id} className="border rounded-3 p-3">
            <div className="fw-semibold mb-2">{index + 1}. {question.text}</div>
            {question.type === 'fill_blank' ? (
              <input
                className="form-control form-control-sm"
                value={answers[String(question._id)]?.textAnswer || ''}
                onChange={(event) => setAnswers((current) => ({ ...current, [String(question._id)]: { textAnswer: event.target.value } }))}
              />
            ) : (
              <div className="d-flex flex-column gap-2">
                {(question.options || []).map((option) => (
                  <label key={option._id} className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name={`quiz-${lesson._id}-${question._id}`}
                      checked={answers[String(question._id)]?.selectedOptionId === String(option._id)}
                      onChange={() => setAnswers((current) => ({ ...current, [String(question._id)]: { selectedOptionId: String(option._id) } }))}
                    />
                    <span className="form-check-label">{option.text}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <button type="button" className="btn btn-primary btn-sm mt-3" disabled={submitting} onClick={submitQuiz}>
        {submitting ? 'Submitting...' : 'Submit Quiz'}
      </button>
    </div>
  );
}
